import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import useMailboxService from 'src/modules/mailer/hooks/useMailboxService';
import useMailboxIntentsQuery, {
    MAILBOX_INTENTS_QUERY_KEY,
} from '../../../hooks/useMailboxIntentsQuery';
import { MAILBOX_MESSAGES_QUERY_KEY } from '../../message-folder/hooks/useMailboxMessages';
import { showMailboxCaseCreatedToast } from '../../MailboxCaseCreatedToast';
import {
    getMailboxIntentItems,
    mailboxMessageHasLinkedCase,
    buildMailboxApplyIntentPayload,
    findMailboxLinkedCase,
    getMessageCases,
    mapMailboxActionError,
    normalizeMailboxLinkedCase,
} from '../../../helpers';
import { MAILBOX_MESSAGE_QUERY_KEY } from './useMailboxMessage';

export { MAILBOX_INTENTS_QUERY_KEY };

const useMailboxIntents = ({
    t,
    messageId,
    message,
}) => {
    const service = useMailboxService();
    const queryClient = useQueryClient();
    const location = useLocation();
    const history = useHistory();
    const [applyingIntent, setApplyingIntent] = useState(null);
    const cases = getMessageCases(message);
    const intents = useMailboxIntentsQuery();

    const applyIntent = async (intent, analysis = null) => {
        if (!messageId || applyingIntent) {
            return findMailboxLinkedCase(cases, intent);
        }

        const existing = findMailboxLinkedCase(cases, intent);

        if (existing) {
            return existing;
        }

        if (mailboxMessageHasLinkedCase(message, cases)) {
            toast.error(t('A case has already been created from this email.'));
            return null;
        }

        setApplyingIntent(intent);

        try {
            const payload = await service.applyMessageIntent(
                messageId,
                buildMailboxApplyIntentPayload(intent, analysis)
            );
            const created = normalizeMailboxLinkedCase(payload, intent);

            await Promise.all([
                queryClient.invalidateQueries([MAILBOX_MESSAGE_QUERY_KEY, messageId]),
                queryClient.invalidateQueries([MAILBOX_MESSAGES_QUERY_KEY]),
                queryClient.invalidateQueries([MAILBOX_INTENTS_QUERY_KEY]),
            ]);

            showMailboxCaseCreatedToast(t, created || { intent }, location, history);

            return created || { intent };
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast.error(mapMailboxActionError(error, t));
            return null;
        } finally {
            setApplyingIntent(null);
        }
    };

    return {
        intents: intents.length ? intents : getMailboxIntentItems(),
        cases,
        applyingIntent,
        isApplying: Boolean(applyingIntent),
        areCasesReady: true,
        applyIntent,
        findCase: (intent) => findMailboxLinkedCase(cases, intent),
    };
};

export default useMailboxIntents;
